import { type ReactNode } from 'react';
import { cn } from '@/utils/cn';
import type { Tier } from '@/hooks/use-pricing';
import {
  formatUSD,
  tierRange,
  dailyEarningsRange,
} from './pricing-tier-format';

function ActiveBadges({
  count,
  tier,
}: {
  count: number;
  tier: Tier;
}): ReactNode {
  const toNext = tier.max !== null ? tier.max - count + 1 : null;
  return (
    <>
      <span className="shrink-0 rounded bg-brand-red/10 px-1.5 py-0.5 text-[9px] font-medium text-brand-red">
        Hoy: {count}
      </span>
      {toNext !== null && toNext > 0 && (
        <span className="hidden text-[9px] text-text-tertiary sm:inline">
          {toNext} más →
        </span>
      )}
    </>
  );
}

const ROW_ACTIVE =
  'bg-white/[0.04] before:absolute before:-left-1.5 before:top-2.5 before:bottom-2.5 before:w-[3px] before:rounded-full before:bg-brand-red';

function TierLabel({
  tier,
  active,
}: {
  tier: Tier;
  active: boolean;
}): ReactNode {
  return (
    <span
      className={cn(
        'whitespace-nowrap text-xs',
        active ? 'font-medium text-text-primary' : 'text-text-tertiary'
      )}
    >
      {tierRange(tier)} creadores/día
    </span>
  );
}

function TierEarnings({
  tier,
  active,
}: {
  tier: Tier;
  active: boolean;
}): ReactNode {
  return (
    <div className="ml-auto flex flex-col items-end leading-tight">
      <span
        className={cn(
          'font-mono text-xs tabular-nums',
          active ? 'font-bold text-text-primary' : 'text-text-secondary'
        )}
      >
        {formatUSD(tier.price_per_lead)}/creador
      </span>
      <span className="font-mono text-[10px] tabular-nums text-text-tertiary/70">
        {dailyEarningsRange(tier)}/día
      </span>
    </div>
  );
}

export function TierRowReadonly({
  tier,
  active,
  todayCreators,
}: {
  tier: Tier;
  active: boolean;
  todayCreators: number | undefined;
}): ReactNode {
  return (
    <div
      className={cn(
        'relative flex items-center gap-sm rounded-lg px-sm py-sm',
        active && ROW_ACTIVE
      )}
    >
      <TierLabel tier={tier} active={active} />
      {active && todayCreators !== undefined && (
        <ActiveBadges count={todayCreators} tier={tier} />
      )}
      <TierEarnings tier={tier} active={active} />
    </div>
  );
}
